/**
 * Generic drag-drop sortable list using @dnd-kit.
 * Wrap items in this component to get drag-to-reorder.
 * Touch-friendly via PointerSensor with activation constraint.
 */
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

// ---------------------------------------------------------------------------
// SortableItem wrapper — used internally by children who need drag handle
// ---------------------------------------------------------------------------
interface SortableItemProps {
  id: string;
  children: (attrs: { dragHandleProps: React.HTMLAttributes<HTMLElement> }) => React.ReactNode;
}

export function SortableItem({ id, children }: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag handle button
// ---------------------------------------------------------------------------
export function DragHandle(props: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className="cursor-grab active:cursor-grabbing touch-none p-1 text-muted-foreground hover:text-foreground rounded"
      aria-label="Kéo để sắp xếp"
      {...props}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// SortableList — main export
// ---------------------------------------------------------------------------
interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (newItems: T[]) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
}

export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  className,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Delay prevents accidental drags on click
      activationConstraint: { distance: 8 },
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableItem key={item.id} id={item.id}>
              {(attrs) => renderItem({ ...item, _dragHandleProps: attrs.dragHandleProps } as T & { _dragHandleProps: React.HTMLAttributes<HTMLElement> }, index)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
